import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const GlitchIntro = () => {
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsActive(false);
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  if (!isActive) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.5 }}
        className="fixed inset-0 z-[9999] pointer-events-none overflow-hidden"
        style={{ mixBlendMode: 'normal' }}
      >
        {/* RGB Split Effect */}
        <motion.div
          className="absolute inset-0"
          animate={{
            clipPath: [
              'inset(0% 0% 0% 0%)',
              'inset(20% 0% 30% 0%)',
              'inset(40% 0% 10% 0%)',
              'inset(0% 0% 50% 0%)',
              'inset(30% 0% 20% 0%)',
              'inset(0% 0% 0% 0%)'
            ]
          }}
          transition={{
            duration: 4.5,
            times: [0, 0.2, 0.4, 0.6, 0.8, 1],
            repeat: 0
          }}
          style={{
            background: 'rgba(255, 0, 0, 0.1)',
            transform: 'translateX(-2px)'
          }}
        />
        
        <motion.div
          className="absolute inset-0"
          animate={{
            clipPath: [
              'inset(0% 0% 0% 0%)',
              'inset(30% 0% 20% 0%)',
              'inset(10% 0% 40% 0%)',
              'inset(50% 0% 0% 0%)',
              'inset(20% 0% 30% 0%)',
              'inset(0% 0% 0% 0%)'
            ]
          }}
          transition={{
            duration: 4.5,
            times: [0, 0.2, 0.4, 0.6, 0.8, 1],
            repeat: 0
          }}
          style={{
            background: 'rgba(0, 255, 255, 0.1)',
            transform: 'translateX(2px)'
          }}
        />

        {/* Scan Lines */}
        <motion.div
          className="absolute inset-0"
          style={{
            backgroundImage: 'repeating-linear-gradient(0deg, rgba(203, 161, 53, 0.03) 0px, transparent 1px, transparent 2px, rgba(203, 161, 53, 0.03) 3px)',
            backgroundSize: '100% 4px'
          }}
          animate={{ 
            backgroundPositionY: ['0px', '100px']
          }}
          transition={{
            duration: 0.75,
            repeat: Infinity,
            ease: 'linear'
          }}
        />

        {/* Horizontal Glitch Lines — stable keys (intro plays once at app load) */}
        {['gl-a', 'gl-b', 'gl-c', 'gl-d', 'gl-e', 'gl-f', 'gl-g', 'gl-h'].map((id, i) => (
          <motion.div
            key={id}
            className="absolute w-full h-[2px] bg-[#cba135]"
            initial={{ 
              top: `${Math.random() * 100}%`,
              opacity: 0,
              scaleX: 0
            }}
            animate={{
              top: [`${Math.random() * 100}%`, `${Math.random() * 100}%`, `${Math.random() * 100}%`],
              opacity: [0, 0.8, 0],
              scaleX: [0, 1, 0],
              x: [0, Math.random() * 100 - 50, 0]
            }}
            transition={{
              duration: 0.45,
              delay: i * 0.2,
              repeat: Math.floor(Math.random() * 2)
            }}
            style={{
              boxShadow: '0 0 10px rgba(203, 161, 53, 0.8)'
            }}
          />
        ))}

        {/* Digital Noise Overlay */}
        <motion.div
          className="absolute inset-0"
          animate={{
            opacity: [0.1, 0.3, 0.1, 0.4, 0.1]
          }}
          transition={{
            duration: 0.3,
            repeat: 4
          }}
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='3.5' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.3'/%3E%3C/svg%3E")`,
            backgroundSize: '200px 200px',
            mixBlendMode: 'overlay'
          }}
        />

        {/* Glitch Text Overlay */}
        <motion.div
          className="absolute inset-0 flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 1, 0, 1, 0] }}
          transition={{ duration: 4.5, times: [0, 0.1, 0.3, 0.5, 1] }}
        >
          <div className="relative">
            <motion.h1
              className="heading-font text-6xl font-bold text-[#cba135]"
              animate={{
                x: [0, -5, 5, -5, 5, 0],
                textShadow: [
                  '0 0 0px rgba(203, 161, 53, 0)',
                  '5px 0 20px rgba(255, 0, 0, 0.5), -5px 0 20px rgba(0, 255, 255, 0.5)',
                  '0 0 0px rgba(203, 161, 53, 0)',
                  '-5px 0 20px rgba(255, 0, 0, 0.5), 5px 0 20px rgba(0, 255, 255, 0.5)',
                  '0 0 0px rgba(203, 161, 53, 0)'
                ]
              }}
              transition={{
                duration: 0.6,
                repeat: 2
              }}
            >
              1:2000 CLUB
            </motion.h1>
            
            {/* Glitch clone layers */}
            <motion.h1
              className="heading-font text-6xl font-bold absolute top-0 left-0 text-red-500"
              style={{ mixBlendMode: 'screen', opacity: 0.3 }}
              animate={{
                x: [-2, 2, -2],
                opacity: [0, 0.5, 0]
              }}
              transition={{
                duration: 0.3,
                repeat: 4
              }}
            >
              1:2000 CLUB
            </motion.h1>
            
            <motion.h1
              className="heading-font text-6xl font-bold absolute top-0 left-0 text-cyan-500"
              style={{ mixBlendMode: 'screen', opacity: 0.3 }}
              animate={{
                x: [2, -2, 2],
                opacity: [0, 0.5, 0]
              }}
              transition={{
                duration: 0.3,
                repeat: 4,
                delay: 0.15
              }}
            >
              1:2000 CLUB
            </motion.h1>
          </div>
        </motion.div>

        {/* VHS Tracking Lines */}
        <motion.div
          className="absolute w-full h-[100px]"
          animate={{
            top: ['-100px', '100%']
          }}
          transition={{
            duration: 3,
            ease: 'linear',
            repeat: 0
          }}
          style={{
            background: 'linear-gradient(to bottom, transparent, rgba(203, 161, 53, 0.1), transparent)',
            filter: 'blur(3px)'
          }}
        />

        {/* Screen Flash */}
        <motion.div
          className="absolute inset-0 bg-[#cba135]"
          animate={{
            opacity: [0, 0.3, 0, 0.2, 0]
          }}
          transition={{
            duration: 4.5,
            times: [0, 0.05, 0.1, 0.15, 0.2]
          }}
        />
      </motion.div>
    </AnimatePresence>
  );
};

export default GlitchIntro;
